import { lazy, Suspense, useState } from 'react';

import { COOKIE_KEYS as CORE_COOKIE_KEYS } from '@core-ui/common';
import { AppMenuProvider, CORE_PAGES } from '@core-ui/components';
import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { MoniebookLoader } from '@moniebook-web/components/molecules/moniebook-loader';
import { useFirstTimeLoginStore } from '@moniebook-web/store';
import { COOKIE_KEYS, PAGES } from '@moniebook-web/types';
import {
    getItem,
    ImplementerTasks,
    PAGES_WITHOUT_SIDEBAR,
    useUserInfo,
} from '@moniebook-web/utils';
import { Navigate, useLocation } from 'react-router';

import { SessionTokenPayloadProvider } from '@/features/ghost-session/context/session-token-payload-context';
import { FEATURE_FLAGS } from '@/shared/constants';

import { AppLayout } from '../app-layout';
import { OnboardingChecks } from './onboarding-checks';
import { SessionManager } from './session-manager';
import { Sidebar } from './sidebar';
import { staticMenus } from './sidebar/submenus';

const FirstTimeWelcomeModal = lazy(() => import('./welcome-modal'));
const SubscriptionNotice = lazy(() => import('./subscription-notice'));
const Walkthrough = lazy(() => import('./walkthrough'));
const BusinessCategoryModal = lazy(
    () => import('./business-category-modal/business-category-modal'),
);

const paths = [
    CORE_PAGES.CHECK_LOGIN,
    CORE_PAGES.ONBOARDING_TASKS,
    PAGES.SETTINGS_HOME,
    '/dashboard/onboarding/business-email',
    '/dashboard/onboarding/metadata',
    CORE_PAGES.BUSINESS.CREATE_FIRST_BRANCH,
    CORE_PAGES.ONBOARDING.CREATE_FIRST_BRANCH,
    CORE_PAGES.ONBOARDING.BUSINESS_DETAILS,
    CORE_PAGES.ONBOARDING.FIRST_BRANCH,
    CORE_PAGES.ONBOARDING.BRM_DETAILS,
    CORE_PAGES.ONBOARDING.RECORD_FIRST_SALE,
    CORE_PAGES.ONBOARDING.SUBSCRIBE,
    CORE_PAGES.ONBOARDING.SUBSCRIBE_CONFIRM,
];

const shouldHideSidebar = (pathname: string) => {
    if (paths.some((name) => pathname.startsWith(name))) {
        return true;
    }

    // eslint-disable-next-line security/detect-object-injection
    return PAGES_WITHOUT_SIDEBAR[pathname];
};

export function Dashboard() {
    const loggedIn = getItem(COOKIE_KEYS.TOKEN_VARIABLE);
    const autoLoggedOut = getItem(CORE_COOKIE_KEYS.AUTO_LOGGED_OUT);
    const location = useLocation();
    const hideSidebar = shouldHideSidebar(location.pathname);

    const { firstTimeLogin, setFirstTimeLogin } = useFirstTimeLoginStore();
    const { businesses, loading } = useUserInfo(!!loggedIn);
    const [isWalkthroughOpen, setIsWalkthroughOpen] = useState(false);

    if (!loggedIn) {
        ImplementerTasks.store(location);
        const url = autoLoggedOut
            ? CORE_PAGES.AUTH.AUTO_LOGGED_OUT
            : PAGES.AUTH_UI.LOGIN;

        return <Navigate to={url} replace />;
    }

    if (loading) {
        return <MoniebookLoader fullHeight />;
    }

    if (businesses?.length) {
        return (
            <SessionTokenPayloadProvider>
                <AppMenuProvider imports={Promise.resolve(staticMenus)}>
                    <AppLayout sidebar={hideSidebar ? undefined : Sidebar} />
                    <OnboardingChecks />
                    <SessionManager />
                    <Suspense>
                        <FirstTimeWelcomeModal
                            isOpen={firstTimeLogin}
                            onClose={() => setFirstTimeLogin(false)}
                            onProceed={() => setIsWalkthroughOpen(true)}
                        />
                    </Suspense>
                    <Suspense>
                        <SubscriptionNotice />
                    </Suspense>
                    <Suspense>
                        <FeatureProtectedView
                            flag={FEATURE_FLAGS.ENABLE_BUSINESS_CATEGORY_MODAL}
                            noAltView>
                            <BusinessCategoryModal />
                        </FeatureProtectedView>
                    </Suspense>
                    <Suspense>
                        <Walkthrough
                            openState={[
                                isWalkthroughOpen,
                                setIsWalkthroughOpen,
                            ]}
                        />
                    </Suspense>
                </AppMenuProvider>
            </SessionTokenPayloadProvider>
        );
    }

    return (
        <AppMenuProvider imports={Promise.resolve(staticMenus)}>
            <AppLayout />
            <OnboardingChecks />
        </AppMenuProvider>
    );
}
